import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Modal } from '@material-ui/core';
import clsx from 'clsx';
import { withAllowedOperationsProvider } from '../../hocs';
import {
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  RESOURCE_TYPE,
  ASN_STATUS,
  AGGREGATE_STATUS,
} from '../../constants';
import moment from 'moment';
import styles from './BulkAssignShiftForm.module.css';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { buildErrorMessage } from '../../apis/calls';

const BulkAssignShiftForm = ({
  open,
  closeForm,
  activeAsnDate,
  shifts,
  asns,
  refreshData,
  bulkAssignShift,
  title,
}) => {
  const { showError, showSuccess } = usePopupManager();
  const [doesAssignmentExceedTotal, setDoesAssignmentExceedTotal] = useState(
    false,
  );

  const [isApplying, setIsApplying] = useState(false);

  const [availableAsnsWithoutShift, setAvailableAsnsWithoutShift] = useState(
    null,
  );
  const [
    assignmentsToShiftLabelsMap,
    setAssignmentsToShiftLabelsMap,
  ] = useState({});

  useEffect(() => {
    if (asns)
      setAvailableAsnsWithoutShift(
        asns.filter(
          asn =>
            (asn.status === ASN_STATUS.PLANNED ||
              asn.status === AGGREGATE_STATUS.ASN_GENERATED) &&
            !asn.shiftName,
        ),
      );
  }, [asns]);

  useEffect(() => {
    setAssignmentsToShiftLabelsMap(
      shifts
        .map(shift => shift.label)
        .reduce((acc, currShift) => ({ ...acc, [currShift]: '' }), {}),
    );
    return () => setAssignmentsToShiftLabelsMap({});
  }, [shifts, open]);

  const getSumOfAssignments = assignmentsMap =>
    Object.values(assignmentsMap)
      .filter(assignment => assignment)
      .reduce((acc, curr) => acc + curr, 0);

  useEffect(() => {
    const sumOfCurrentAssignments = getSumOfAssignments(
      assignmentsToShiftLabelsMap,
    );

    setDoesAssignmentExceedTotal(
      sumOfCurrentAssignments > availableAsnsWithoutShift?.length,
    );
  }, [availableAsnsWithoutShift, assignmentsToShiftLabelsMap]);

  const handleCancel = () => closeForm();

  const handleApply = async () => {
    if (doesAssignmentExceedTotal || !availableAsnsWithoutShift?.length) return;

    const sumOfCurrentAssignments = getSumOfAssignments(
      assignmentsToShiftLabelsMap,
    );

    if (sumOfCurrentAssignments === 0) return;

    try {
      setIsApplying(true);
      const requiredAvailableAsns = availableAsnsWithoutShift.slice(
        0,
        sumOfCurrentAssignments,
      );

      const sortedRequiredShifts = shifts.sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });

      await bulkAssignShift(
        sortedRequiredShifts,
        requiredAvailableAsns,
        assignmentsToShiftLabelsMap,
      );

      closeForm();
      showSuccess({
        contextText: DISPLAY_MESSAGES.SHIFT_ASSIGNMENT_SUCCESS,
      });
      refreshData();
    } catch (error) {
      showError({ contextText: 'Failed to assign shifts', error });
      console.error(
        'failed to bulk assign shifts, error:' + buildErrorMessage(error),
      );
    } finally {
      setIsApplying(false);
    }
  };

  const deriveFormSubTitles = date => {
    if (!date) {
      return ['No', ` ${title}s`];
    }
    let monthTitle = '';
    let dateTitle = '';
    if (date?.isBefore(moment().format(DATE_FORMAT.ISO))) {
      monthTitle = 'Prev.';
      dateTitle = `${title}`;
    } else {
      monthTitle = date.format('MMMM');
      dateTitle = date.format('DD');
    }

    return [monthTitle, dateTitle];
  };

  const [month, date] = deriveFormSubTitles(activeAsnDate);

  const isWholeNumber = val => /^[0-9]*$/.test(val);

  const handleShiftAssignment = shift => e => {
    const { value } = e.target;
    if (!isWholeNumber(value)) {
      window.alert('Pls enter whole numbers only.');
      return;
    }
    setAssignmentsToShiftLabelsMap(prev => ({
      ...prev,
      [shift.label]: Number(value),
    }));
  };

  const renderShiftRow = shift => {
    return (
      <div key={shift.label} className={styles.row}>
        <div className={styles.labelBlue}>{shift.label} Shift</div>
        <div className={styles.description}>
          <input
            data-testid={`input-for-shift-${shift.label}`}
            value={assignmentsToShiftLabelsMap[shift.label]}
            type="text"
            placeholder="(Enter Qty)"
            className={clsx(
              styles.textfield,
              doesAssignmentExceedTotal && styles.errorTextfield,
            )}
            onChange={handleShiftAssignment(shift)}
          ></input>
        </div>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={closeForm}>
      <div className={styles.formContainer} data-testid="asssign-shift-modal">
        <div className={styles.formTitle}>
          <span>Bulk Assignment</span>
        </div>
        <div>
          <div className={styles.dateRow}>
            <div className={styles.dateCol}>
              <span className={styles.dateSuper}>{month}</span>
              <div className={styles.date}>{date}</div>
            </div>
            <div className={styles.dateDescription}>
              {`Assignment will be made from first “to be planned” ${title}/Available
              ${title}`}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.label}>
              {`Available ${title}s for Shift assignment`}
            </div>
            <div className={styles.descriptionStrong}>
              {availableAsnsWithoutShift?.length || '- -'}
            </div>
          </div>
          <div className={styles.defineRow}>
            <div className={styles.label}>Define</div>
            <div className={styles.description}>
              {doesAssignmentExceedTotal && (
                <span className={styles.error}>
                  {`“Assigned ${title} count exceeds Available ${title}s”`}
                </span>
              )}
            </div>
          </div>
          {shifts.map(shift => renderShiftRow(shift))}
          <div className={styles.bottomRow}>
            <Button
              className={clsx(styles.button, styles.cancleButton)}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              className={clsx(styles.button, styles.applyButton)}
              data-testid="bulk-assign-shift-btn"
              onClick={handleApply}
            >
              {isApplying ? (
                <CircularProgress size="1.3rem" style={{ color: 'white' }} />
              ) : (
                'Apply'
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

BulkAssignShiftForm.propTypes = {
  open: PropTypes.bool.isRequired,
  closeForm: PropTypes.func.isRequired,
  activeAsnDate: PropTypes.any,
  shifts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      isAvailable: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  refreshData: PropTypes.func.isRequired,
  asns: PropTypes.arrayOf(
    PropTypes.shape({
      asnId: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      shift: PropTypes.string,
    }),
  ),
  bulkAssignShift: PropTypes.func.isRequired,
};

export { BulkAssignShiftForm as PureBulkAssignShiftForm };

export default withAllowedOperationsProvider(
  BulkAssignShiftForm,
  false,
  RESOURCE_TYPE.CHASSIS,
);
